import { OrgAdminFragment } from '@alice-financial/alice-sdk'
import {
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FullDialog,
  List,
  ListItem,
  Typography,
} from '@alice-financial/pretext-ui'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { NewAdminInputs } from '../aliceCardProgram/KYC/AddNewAdminForm'
import { useOrg } from '../organization/useOrg'
import { useCreateOrgAdmin, UseCreateOrgAdminInput } from './useCreateOrgAdmin'

type AdminSelectorProps = {
  selectedAdmins: Array<OrgAdminFragment> | undefined
  admins: Array<OrgAdminFragment>
  onChange: (admin: UseCreateOrgAdminInput) => void
  isSaving: boolean
}

const AdminSelector = ({ selectedAdmins = [], admins, onChange, isSaving }: AdminSelectorProps) => {
  return (
    <List>
      {admins.map((admin) => (
        <ListItem key={admin.id} disablePadding disableGutters>
          <Checkbox
            label={`${admin.firstName} ${admin.lastName} (${admin.email})`}
            checked={selectedAdmins.includes(admin)}
            disabled={isSaving}
            onChange={(event) =>
              onChange({
                firstName: admin.firstName || '',
                lastName: admin.lastName || '',
                email: admin.email || '',
                roleLabel: admin.role?.label || '',
                remove: !event.target.checked,
              })
            }
          />
        </ListItem>
      ))}
    </List>
  )
}
type AdminRoleDialogProps = {
  roleTitle: string
  roleDescription?: string | undefined
  admins: Array<OrgAdminFragment>
  open: boolean
  onClose: () => void
}

export const AdminRoleDialog = ({
  roleTitle,
  roleDescription = ' ',
  admins,
  open,
  onClose,
}: AdminRoleDialogProps) => {
  const org = useOrg()
  const isAssigned = (admin: OrgAdminFragment) => Boolean(admin.role?.label?.includes(roleTitle))
  const assignedAdmins = admins.filter(isAssigned)
  const { mutate: createOrgAdmin, isLoading: isSaving } = useCreateOrgAdmin(
    { roleTitle },
    { onSuccess: onClose }
  )
  const { control, handleSubmit, watch } = useForm({
    defaultValues: { firstName: '', lastName: '', email: '' },
  })
  const newAdminValues = watch(['email', 'firstName', 'lastName'])
  const requireNewAdminValues = newAdminValues.some(Boolean)

  const onSubmit = handleSubmit((data) =>
    data.email ? createOrgAdmin({ ...data, roleLabel: '' }) : onClose()
  )
  return (
    <FullDialog
      open={open}
      onClose={onClose}
      title={
        <>
          <Typography variant="h2">Select {roleTitle}</Typography>
          <Typography variant="body2">{roleDescription}</Typography>
        </>
      }
    >
      <Container>
        <AdminSelector
          selectedAdmins={assignedAdmins}
          admins={admins}
          aria-label={`Select ${roleTitle}`}
          onChange={(adminInput) => createOrgAdmin(adminInput)}
          isSaving={isSaving}
        />
        <Divider sx={{ mt: 2, mb: 3 }} />

        <Typography variant="body2" gutterBottom>
          <strong>Or, assign a new person to {roleTitle}</strong>
        </Typography>
        <form onSubmit={onSubmit}>
          <Box pb={5}>{org && <NewAdminInputs control={control} required={requireNewAdminValues} />}</Box>
          <Box pb={2} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              Assign
            </Button>
          </Box>
        </form>
      </Container>
    </FullDialog>
  )
}
